/**
 * 老师 / 我 —— A/B 对比试听
 * ============================================================
 * 做法对齐 Midoo（截图观察）：跟读之后，并排给两个播放键 ——
 * 「老师」放示范音，「我」放自己刚录的那遍。
 *
 * ── 为什么这个功能值得做 ────────────────────────────────────
 * 语言学习里"听出自己的问题"比"被告知哪里错"更有效：
 *   · 我们的声调分析准确率只有约 33%，靠它打分不可靠（见 config/sessions.js）
 *   · 但**把两段音放在一起让用户自己比**，不需要任何评测算法就是准的 ——
 *     用户自己听得出差别，这是零误差的反馈
 * 所以这个组件**不做任何评分**，只负责把两段音顺次播出来。
 *
 * ── 与页面的分工 ────────────────────────────────────────────
 *   「老师」音走页面的 speak()：它已处理方言 instruction、口型同步、
 *   自动播放被拦截的兜底。本组件不重复实现 TTS。
 *   「我」音是纯本地回放，**不触发口型**（虚拟人不该对用户的录音张嘴）。
 *
 * ── 播放状态从哪来 ──────────────────────────────────────────
 *   老师：用页面传入的 `speaking`。speak() 内部 await 的是 play() 的
 *         resolve（播放**开始**），不是播放结束，所以组件自己拿不到结束时机，
 *         必须由页面在 onended 里把 speaking 置回 false。
 *   我  ：本组件自己维护，onended 里清理。
 */

import { useEffect, useRef, useState } from 'react';

const BTN_BASE =
  'flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition active:scale-[0.97] disabled:opacity-40';

export default function AbCompare({
  teacherText,
  audioUrl,
  onSpeak,
  speaking = false,
  onStopTeacher,
  disabled = false,
  className = '',
}) {
  const [playingMine, setPlayingMine] = useState(false);
  const mineRef = useRef(null);

  /** 停掉自己的回放，并复位状态 */
  const stopMine = () => {
    const audio = mineRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch {
        /* 音频尚未就绪时设置 currentTime 会抛，忽略即可 */
      }
      mineRef.current = null;
    }
    setPlayingMine(false);
  };

  /*
   * 卸载时必须停掉回放，否则会出现"页面都退了还有声音"的幽灵播放。
   * 换录音（audioUrl 变化）时也要停 —— 那说明用户重新录了一遍，
   * 旧音频对象已经指向废弃的 blob URL。
   */
  useEffect(() => stopMine, []);
  useEffect(() => {
    stopMine();
  }, [audioUrl]);

  /** 播老师示范 */
  const playTeacher = () => {
    if (disabled) return;
    stopMine();
    // speak 可能被浏览器拦截（页面内已有兜底提示），这里不吞异常
    Promise.resolve(onSpeak?.(teacherText, 'cantonese')).catch(() => {});
  };

  /** 播我自己录的 */
  const playMine = () => {
    if (disabled || !audioUrl) return;

    // 再点一次 = 停止
    if (mineRef.current) {
      stopMine();
      return;
    }

    // 自己的音和老师的音不该重叠，先打断老师
    onStopTeacher?.();

    const audio = new Audio(audioUrl);
    mineRef.current = audio;
    audio.onended = () => stopMine();
    audio.onerror = () => stopMine();
    setPlayingMine(true);
    audio.play().catch(() => stopMine());
  };

  const hasMine = !!audioUrl;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={playTeacher}
        disabled={disabled || !teacherText}
        aria-label="听老师示范"
        className={`${BTN_BASE} ${
          speaking
            ? 'bg-primary text-white'
            : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-200'
        }`}
      >
        <span className="material-icons !text-sm">{speaking ? 'volume_up' : 'play_arrow'}</span>
        老师
      </button>

      <button
        type="button"
        onClick={playMine}
        disabled={disabled || !hasMine}
        aria-label={hasMine ? '听我录的' : '还没有录音'}
        title={hasMine ? '再点一次停止' : '先按住麦克风说一句'}
        className={`${BTN_BASE} ${
          playingMine
            ? 'bg-primary text-white'
            : hasMine
              ? 'bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-200'
              : 'bg-zinc-50 text-zinc-300 dark:bg-zinc-800 dark:text-zinc-600'
        }`}
      >
        <span className="material-icons !text-sm">
          {playingMine ? 'graphic_eq' : hasMine ? 'play_arrow' : 'mic_none'}
        </span>
        我
      </button>
    </div>
  );
}
